import { useCallback, useEffect, useState } from 'react';
import { apiClient, API_BASE_URL } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, hasRole, isSystemAdmin } from '../utils/permissions';

const POLL_MS = 45000;
const HEARTBEAT_MS = 60000;

export function canViewPresence(user) {
  if (!user) return false;
  if (isSystemAdmin(user) || hasRole(user, 'gerente')) return true;
  return hasPermission(user, 'monitoring.presence');
}

/**
 * Heartbeat for all authenticated users + active-users list for Admin/Gerente.
 */
export function usePresence() {
  const { user, token, isAuthenticated } = useAuth();
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const canView = canViewPresence(user);

  const sendHeartbeat = useCallback(async () => {
    if (!token || document.visibilityState !== 'visible') return;
    try {
      await apiClient(`${API_BASE_URL}/api/auth/heartbeat`, { method: 'POST', body: '{}' });
    } catch {
      /* best-effort */
    }
  }, [token]);

  const loadActive = useCallback(async () => {
    if (!token || !canView) {
      setActiveUsers([]);
      setActiveCount(0);
      return;
    }
    try {
      const data = await apiClient(`${API_BASE_URL}/api/presence/active`);
      const users = Array.isArray(data?.users) ? data.users : [];
      setActiveUsers(users);
      setActiveCount(Number(data?.count) || users.length);
    } catch {
      setActiveUsers([]);
      setActiveCount(0);
    }
  }, [token, canView]);

  useEffect(() => {
    if (!isAuthenticated || !token) return undefined;

    sendHeartbeat();
    const beatId = setInterval(sendHeartbeat, HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') sendHeartbeat();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(beatId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, token, sendHeartbeat]);

  useEffect(() => {
    if (!isAuthenticated || !token || !canView) {
      setActiveUsers([]);
      setActiveCount(0);
      return undefined;
    }

    loadActive();
    const pollId = setInterval(loadActive, POLL_MS);
    return () => clearInterval(pollId);
  }, [isAuthenticated, token, canView, loadActive]);

  return {
    canView,
    activeUsers,
    activeCount,
    refreshActive: loadActive,
  };
}
